import React, { useMemo, useState } from 'react'
import axios from 'axios'

// ---------------------------------------------------------------------------
// DEV-ONLY end-to-end permutation test runner.
//
// This page fires form permutations at the REAL backend /submit-lead endpoint
// (the exact request the review -> signature button makes). A 200 with
// claimData means that permutation would proceed past the review page to the
// e-sign step. Anything else (400/500/timeout) is exactly what blocks a real
// user before e-sign.
//
// IMPORTANT — this runner is intentionally isolated from the real form:
//   * it lives on its own /test route, outside the <Main> layout/providers
//   * it never imports or mutates the form's shared claim object/state
//   * it rebuilds the payload itself, faithfully mirroring what the real form
//     POSTs in each variation — including the known serialization quirks:
//       - previous_addresses sent as a JSON STRING (not an array)
//       - the prev_* working fields leaking into the payload
//       - the signature-step consent fields (acceptTerms, privacyPolicyConsent,
//         noDuplicateClaimConsent) leaking in once the signature step mounts
//       - postcodes saved untrimmed / with raw Data8 (PAF) whitespace
//
// NOTE: each run creates real client/lead rows in whatever DB the configured
// API points at (REACT_APP_API_UPLOAD_DATA_URL) and fires the OpenAI / boshhh
// / S3 work for each variation. Run against DEV only. Every row uses a unique
// email so the duplicate-client guard never masks a result (except cases that
// explicitly test the email value).
// ---------------------------------------------------------------------------

const FORM_VERSION = '1.1'
const CONSENT_WORDING_VERSION = '1.1'

const API_URL = process.env.REACT_APP_API_UPLOAD_DATA_URL || 'http://localhost:8080/pcp-webform/submit-lead'

type Claim = Record<string, any>

type PrevMode = 'none' | 'jsonString' | 'array'

interface CaseFlags {
    // Models the previous-address sub-form. 'jsonString' is what the REAL form
    // sends today; 'array' is the corrected shape; 'none' = never touched.
    prevMode?: PrevMode
    // Models the prev_* working fields leaking into the shared claim object
    // (happens when the user types into the previous-address form).
    leakPrevFields?: boolean
    // Models the signature step having mounted before submit, leaking
    // acceptTerms / privacyPolicyConsent / noDuplicateClaimConsent.
    signatureLeak?: 'no' | 'yes' | false
}

interface TestCase {
    group: string
    label: string
    overrides: Claim
    flags?: CaseFlags
    // Optional declared expectation so the table can highlight regressions /
    // confirm fixes. Omit when the correct behaviour is "don't care".
    expectPass?: boolean
}

interface TestResult {
    label: string
    group: string
    status: number | string
    reachesEsign: boolean
    expectPass?: boolean
    matchedExpectation?: boolean
    message: string
    ms: number
}

const coerceYesTrue = (v: any) => v === 'yes' || v === true

// Mirror of src/helpers/uploadFile.ts so the request is byte-for-byte identical
// to what production sends (consent + channel coercion, version stamps). It does
// NOT strip unknown keys — matching the real form — so leaked fields survive.
const applyUploadCoercion = (claim: Claim): Claim => {
    const payload: Claim = { ...claim }

    if (payload.consentToContactAboutThisClaim !== undefined) {
        payload.consentToContactAboutThisClaim = coerceYesTrue(payload.consentToContactAboutThisClaim)
    }
    for (const channel of ['sms_opt_in', 'phone_opt_in', 'email_opt_in', 'whatsapp_opt_in']) {
        payload[channel] = coerceYesTrue(payload[channel])
    }
    if (payload.sentinel_referral_consent !== undefined) {
        payload.sentinel_referral_consent = coerceYesTrue(payload.sentinel_referral_consent)
    }

    payload.form_version = FORM_VERSION
    payload.consent_wording_version_id = CONSENT_WORDING_VERSION
    return payload
}

// A clean, fully-valid baseline that reaches e-sign. Channels are stored as
// 'yes'/'no' strings exactly like the form's Checkbox component does.
const baseClaim = (): Claim => ({
    leadOrigin: 'webform_MFC',
    leadSource: window.location.origin + '/',
    title: 'Mr',
    firstname: 'Test',
    lastname: 'User',
    dob: '02/02/1990',
    telephone: '07907701635',
    email: 'placeholder@example.co.uk',
    address_line_one: 'Flat C',
    address_line_two: 'Selby Lodge',
    address_line_three: 'Cambray Place',
    city: 'Cheltenham',
    county: 'Gloucestershire',
    postcode: 'GL50 1JN',
    consentToContactAboutThisClaim: 'yes',
    sentinel_referral_consent: 'yes',
    sms_opt_in: 'no',
    phone_opt_in: 'no',
    email_opt_in: 'yes',
    whatsapp_opt_in: 'no',
})

const samplePreviousAddress = () => ({
    text: '1 Old Road, Bath, BA1 1AA',
    line_one: '1 Old Road',
    line_two: '',
    line_three: '',
    town: 'Bath',
    county: 'Somerset',
    postcode: 'BA1 1AA',
    house_number: '1',
    flat_number: '',
})

// Given a test case, build the exact raw claim the real form would hold in that
// state, then apply the uploadFile coercion. This is where form-fidelity lives.
const buildPayload = (tc: TestCase, index: number): Claim => {
    const claim: Claim = { ...baseClaim(), ...tc.overrides }

    // Explicit `undefined` overrides simulate "key never sent".
    Object.keys(tc.overrides).forEach((k) => {
        if (tc.overrides[k] === undefined) delete claim[k]
    })

    const flags = tc.flags || {}

    // Previous-address sub-form behaviour.
    if (flags.prevMode === 'jsonString') {
        claim.previous_addresses = JSON.stringify([samplePreviousAddress()])
    } else if (flags.prevMode === 'array') {
        claim.previous_addresses = [samplePreviousAddress()]
    }

    // prev_* working fields leaking into the shared claim object.
    if (flags.leakPrevFields) {
        const p = samplePreviousAddress()
        claim.prev_address_line_one = p.line_one
        claim.prev_address_line_two = p.line_two
        claim.prev_address_line_three = p.line_three
        claim.prev_city = p.town
        claim.prev_county = p.county
        claim.prev_postcode = p.postcode
    }

    // Signature-step consent fields leaking once that step mounts.
    if (flags.signatureLeak === 'no' || flags.signatureLeak === 'yes') {
        claim.acceptTerms = flags.signatureLeak
        claim.privacyPolicyConsent = flags.signatureLeak
        claim.noDuplicateClaimConsent = flags.signatureLeak
    }

    // Unique email unless the case is specifically exercising the email value,
    // so the duplicate-client guard can't mask the real validation result.
    if (!('email' in tc.overrides)) {
        claim.email = `test_${Date.now()}_${index}@example.co.uk`
    }

    return applyUploadCoercion(claim)
}

// ---------------------------------------------------------------------------
// Permutation generators
// ---------------------------------------------------------------------------

const CHANNELS = ['sms_opt_in', 'phone_opt_in', 'email_opt_in', 'whatsapp_opt_in'] as const

const channelMaskOverrides = (mask: number): { overrides: Claim; picked: string[] } => {
    const overrides: Claim = {}
    const picked: string[] = []
    CHANNELS.forEach((c, i) => {
        const on = Boolean(mask & (1 << i))
        overrides[c] = on ? 'yes' : 'no'
        if (on) picked.push(c.replace('_opt_in', ''))
    })
    return { overrides, picked }
}

// 1. Every contact-channel opt-in combination (16) + the never-toggled case.
const channelCases = (): TestCase[] => {
    const cases: TestCase[] = []
    for (let mask = 0; mask < 16; mask++) {
        const { overrides, picked } = channelMaskOverrides(mask)
        cases.push({
            group: 'channels',
            label: `channels: [${picked.join(', ') || 'none'}]`,
            overrides,
            expectPass: true,
        })
    }
    cases.push({
        group: 'channels',
        label: 'channels: all UNDEFINED (never toggled)',
        overrides: { sms_opt_in: undefined, phone_opt_in: undefined, email_opt_in: undefined, whatsapp_opt_in: undefined },
        expectPass: true,
    })
    return cases
}

// 2. Every title option.
const titleCases = (): TestCase[] =>
    ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr'].map((title) => ({
        group: 'titles',
        label: `title: ${title}`,
        overrides: { title },
        expectPass: true,
    }))

// 3. Postcode variants, incl. real Data8 (PAF) whitespace artifacts.
const postcodeCases = (): TestCase[] => {
    const list: Array<[string, string, boolean]> = [
        // label, postcode, expectPass
        ['normal "GL50 1JN"', 'GL50 1JN', true],
        ['no space "GL501JN"', 'GL501JN', true],
        ['lowercase "gl50 1jn"', 'gl50 1jn', true],
        ['London "EC1A 1BB"', 'EC1A 1BB', true],
        ['short outward double space "G5  8AS"', 'G5  8AS', false],
        ['short outward double space "M1  1AE"', 'M1  1AE', false],
        ['trailing space "GL50 1JN "', 'GL50 1JN ', true],
        ['leading space " GL50 1JN"', ' GL50 1JN', true],
        ['incomplete outward only "SK8 "', 'SK8 ', false],
        ['excluded letters "GL50 1CC"', 'GL50 1CC', false],
        ['stray text "GL50 1JN UK"', 'GL50 1JN UK', false],
        ['empty', '', false],
    ]
    return list.map(([label, postcode, expectPass]) => ({
        group: 'postcode',
        label: `postcode ${label}`,
        overrides: { postcode },
        expectPass,
    }))
}

// 4. Telephone variants.
const phoneCases = (): TestCase[] => {
    const list: Array<[string, string, boolean]> = [
        ['plain "07907701635"', '07907701635', true],
        ['with spaces "07907 701635"', '07907 701635', true],
        ['intl "+44 7907 701635"', '+44 7907 701635', true],
        ['leading paren "(07907) 701635"', '(07907) 701635', false],
        ['with letters "07907 70AB35"', '07907 70AB35', false],
        ['too short "079"', '079', false],
        ['blanked (empty)', '', false],
    ]
    return list.map(([label, telephone, expectPass]) => ({
        group: 'telephone',
        label: `phone ${label}`,
        overrides: { telephone },
        expectPass,
    }))
}

// 5. Email variants.
const emailCases = (): TestCase[] => {
    const list: Array<[string, string, boolean | undefined]> = [
        ['standard', `test_email_${Date.now()}@example.co.uk`, true],
        ['plus tag', `plus+tag_${Date.now()}@example.co.uk`, undefined], // passes Joi; may hit duplicate logic
        ['single-label domain "user@localhost"', 'user@localhost', false],
        ['no @', 'not-an-email', false],
        ['blanked (empty)', '', false],
    ]
    return list.map(([label, email, expectPass]) => ({
        group: 'email',
        label: `email ${label}`,
        overrides: { email },
        expectPass,
    }))
}

// 6. DOB variants.
const dobCases = (): TestCase[] => {
    const list: Array<[string, string, boolean]> = [
        ['dd/mm/yyyy "02/02/1990"', '02/02/1990', true],
        ['single digits "2/2/1990"', '2/2/1990', true],
        ['dashes "02-02-1990"', '02-02-1990', true],
        ['ISO "1990-02-02"', '1990-02-02', true],
        ['2-digit year "02/02/90"', '02/02/90', false],
        ['blanked (empty)', '', false],
    ]
    return list.map(([label, dob, expectPass]) => ({
        group: 'dob',
        label: `dob ${label}`,
        overrides: { dob },
        expectPass,
    }))
}

// 7. leadSource variants.
const leadSourceCases = (): TestCase[] => {
    const list: Array<[string, string | undefined, boolean]> = [
        ['valid https', 'https://mfc-advice.sentinellegal.co.uk/', true],
        ['no scheme', 'mfc-advice.sentinellegal.co.uk', false],
        ['webview scheme "capacitor://"', 'capacitor://localhost/', false],
        ['over 2048 chars', 'https://x.co/?q=' + 'a'.repeat(2100), false],
        ['undefined (not sent)', undefined, false],
    ]
    return list.map(([label, leadSource, expectPass]) => ({
        group: 'leadSource',
        label: `leadSource ${label}`,
        overrides: { leadSource },
        expectPass,
    }))
}

// 8. Address line / city variants.
const addressCases = (): TestCase[] => {
    const list: Array<[string, Claim, boolean]> = [
        ['city empty', { city: '' }, false],
        ['city leading space " GLASGOW"', { city: ' GLASGOW' }, true],
        ['address_line_one empty', { address_line_one: '' }, false],
        ['address_line_two leading space', { address_line_two: ' 240 Wallace Street' }, true],
        ['address_line_two empty', { address_line_two: '' }, true],
        ['county empty', { county: '' }, true],
    ]
    return list.map(([label, overrides, expectPass]) => ({
        group: 'address',
        label,
        overrides,
        expectPass,
    }))
}

// 9. Consent variants.
const consentCases = (): TestCase[] => [
    { group: 'consents', label: 'consentToContact = no', overrides: { consentToContactAboutThisClaim: 'no' }, expectPass: false },
    { group: 'consents', label: 'consentToContact = undefined', overrides: { consentToContactAboutThisClaim: undefined }, expectPass: false },
    { group: 'consents', label: 'referral_consent = no', overrides: { sentinel_referral_consent: 'no' }, expectPass: false },
    { group: 'consents', label: 'referral_consent = undefined', overrides: { sentinel_referral_consent: undefined }, expectPass: false },
]

// 10. Previous-address modes (the real serialization + leaks).
const previousAddressCases = (): TestCase[] => [
    { group: 'previous address', label: 'none (never touched)', overrides: {}, flags: { prevMode: 'none' }, expectPass: true },
    { group: 'previous address', label: 'added as JSON string (current form behaviour)', overrides: {}, flags: { prevMode: 'jsonString' }, expectPass: false },
    { group: 'previous address', label: 'added as real array', overrides: {}, flags: { prevMode: 'array' }, expectPass: true },
    { group: 'previous address', label: 'prev_* working fields leaked', overrides: {}, flags: { leakPrevFields: true }, expectPass: false },
    { group: 'previous address', label: 'JSON string + prev_* leak (real combo)', overrides: {}, flags: { prevMode: 'jsonString', leakPrevFields: true }, expectPass: false },
]

// 11. Signature-step leak.
const signatureLeakCases = (): TestCase[] => [
    { group: 'signature leak', label: 'signature fields leaked = no', overrides: {}, flags: { signatureLeak: 'no' }, expectPass: false },
    { group: 'signature leak', label: 'signature fields leaked = yes', overrides: {}, flags: { signatureLeak: 'yes' }, expectPass: false },
]

// 12. FULL structural cartesian: channels (16) x prevMode (3) x signatureLeak
// (off/no). This reproduces real-world combinations of the structural axes.
// Optionally also crossed with every title (x5).
const structuralCartesian = (withTitles: boolean): TestCase[] => {
    const cases: TestCase[] = []
    const prevModes: PrevMode[] = ['none', 'jsonString', 'array']
    const sigLeaks: Array<CaseFlags['signatureLeak']> = [false, 'no']
    const titles = withTitles ? ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr'] : ['Mr']

    for (const title of titles) {
        for (let mask = 0; mask < 16; mask++) {
            const { overrides, picked } = channelMaskOverrides(mask)
            for (const prevMode of prevModes) {
                for (const signatureLeak of sigLeaks) {
                    const expectPass = prevMode !== 'jsonString' && signatureLeak === false
                    cases.push({
                        group: 'structural cartesian',
                        label: `${title} · ch[${picked.join(',') || 'none'}] · prev:${prevMode} · sig:${signatureLeak || 'clean'}`,
                        overrides: { title, ...overrides },
                        flags: { prevMode, signatureLeak },
                        expectPass,
                    })
                }
            }
        }
    }
    return cases
}

// ---------------------------------------------------------------------------

interface Battery {
    key: string
    label: string
    build: () => TestCase[]
    defaultOn: boolean
}

const TestRunner = () => {
    const [running, setRunning] = useState(false)
    const [results, setResults] = useState<TestResult[]>([])
    const [progress, setProgress] = useState('')
    const [withTitlesCross, setWithTitlesCross] = useState(false)

    const batteries: Battery[] = useMemo(
        () => [
            { key: 'channels', label: 'Channel opt-in combinations (17)', build: channelCases, defaultOn: true },
            { key: 'titles', label: 'Title options (5)', build: titleCases, defaultOn: true },
            { key: 'postcode', label: 'Postcode variants (12)', build: postcodeCases, defaultOn: true },
            { key: 'telephone', label: 'Telephone variants (7)', build: phoneCases, defaultOn: true },
            { key: 'email', label: 'Email variants (5)', build: emailCases, defaultOn: true },
            { key: 'dob', label: 'DOB variants (6)', build: dobCases, defaultOn: true },
            { key: 'leadSource', label: 'leadSource variants (5)', build: leadSourceCases, defaultOn: true },
            { key: 'address', label: 'Address line / city variants (6)', build: addressCases, defaultOn: true },
            { key: 'consents', label: 'Consent variants (4)', build: consentCases, defaultOn: true },
            { key: 'previous address', label: 'Previous-address modes + leaks (5)', build: previousAddressCases, defaultOn: true },
            { key: 'signature leak', label: 'Signature-step field leaks (2)', build: signatureLeakCases, defaultOn: true },
            {
                key: 'structural cartesian',
                label: 'FULL structural cartesian (channels × prev × sig)',
                build: () => structuralCartesian(withTitlesCross),
                defaultOn: false,
            },
        ],
        [withTitlesCross],
    )

    const [enabled, setEnabled] = useState<Record<string, boolean>>(() =>
        Object.fromEntries([
            ['channels', true],
            ['titles', true],
            ['postcode', true],
            ['telephone', true],
            ['email', true],
            ['dob', true],
            ['leadSource', true],
            ['address', true],
            ['consents', true],
            ['previous address', true],
            ['signature leak', true],
            ['structural cartesian', false],
        ]),
    )

    const selectedCases = useMemo(() => {
        const cases: TestCase[] = []
        batteries.forEach((b) => {
            if (enabled[b.key]) cases.push(...b.build())
        })
        return cases
    }, [batteries, enabled])

    const runTests = async () => {
        setRunning(true)
        setResults([])
        const cases = selectedCases
        const collected: TestResult[] = []

        for (let i = 0; i < cases.length; i++) {
            const tc = cases[i]
            setProgress(`Running ${i + 1} / ${cases.length}: ${tc.label}`)

            const payload = buildPayload(tc, i)
            const start = performance.now()

            try {
                const response = await axios({ method: 'post', url: API_URL, data: payload, timeout: 60000 })
                const ms = Math.round(performance.now() - start)
                const data: any = response.data
                const reachesEsign = response.status === 200 && data?.success === true && !!data?.claimData
                collected.push({
                    label: tc.label,
                    group: tc.group,
                    status: response.status,
                    reachesEsign,
                    expectPass: tc.expectPass,
                    matchedExpectation: tc.expectPass === undefined ? undefined : tc.expectPass === reachesEsign,
                    message: reachesEsign ? 'OK -> would reach e-sign' : `200 but no claimData: ${JSON.stringify(data)}`,
                    ms,
                })
            } catch (error: any) {
                const ms = Math.round(performance.now() - start)
                const status = error?.response?.status ?? error?.code ?? 'NETWORK/timeout'
                const body = error?.response?.data
                const message = (body && (body.message || JSON.stringify(body))) || error?.message || 'Unknown error'
                collected.push({
                    label: tc.label,
                    group: tc.group,
                    status,
                    reachesEsign: false,
                    expectPass: tc.expectPass,
                    matchedExpectation: tc.expectPass === undefined ? undefined : tc.expectPass === false,
                    message: String(message),
                    ms,
                })
            }

            setResults([...collected])
        }

        setProgress('')
        setRunning(false)
    }

    const copyResults = () => {
        const header = ['group', 'variation', 'status', 'reachesEsign', 'expectPass', 'matched', 'reason', 'ms'].join('\t')
        const rows = results.map((r) =>
            [r.group, r.label, r.status, r.reachesEsign, r.expectPass ?? '', r.matchedExpectation ?? '', r.message, r.ms].join('\t'),
        )
        navigator.clipboard.writeText([header, ...rows].join('\n'))
    }

    const passed = results.filter((r) => r.reachesEsign)
    const blocked = results.filter((r) => !r.reachesEsign)
    const regressions = results.filter((r) => r.matchedExpectation === false)

    const card: React.CSSProperties = {
        border: '1px solid #e6e6ef',
        borderRadius: 12,
        padding: 16,
        marginTop: 16,
        background: '#fff',
    }

    return (
        <div style={{ maxWidth: 1000, margin: '40px auto', padding: 24, fontFamily: 'system-ui, sans-serif', color: '#1a1a2e' }}>
            <h1 style={{ fontSize: 24, marginBottom: 4 }}>MFC Form Permutation Test Runner</h1>
            <p style={{ color: '#555', fontSize: 14, lineHeight: 1.5 }}>
                Fires each selected permutation at <code>{API_URL}</code> exactly like the review &rarr; signature button, faithfully
                reproducing the real form's payload (including JSON-string <code>previous_addresses</code>, leaked <code>prev_*</code> /
                signature fields, and raw postcode whitespace). A <strong>200 with claimData</strong> means the variation reaches e-sign.
                Creates real rows in the dev database.
            </p>

            <div style={card}>
                <p style={{ fontWeight: 700, marginTop: 0 }}>Test batteries</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                    {batteries.map((b) => (
                        <label key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                            <input
                                type="checkbox"
                                checked={!!enabled[b.key]}
                                onChange={(e) => setEnabled((prev) => ({ ...prev, [b.key]: e.target.checked }))}
                            />
                            {b.label}
                        </label>
                    ))}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 12, color: '#666' }}>
                    <input type="checkbox" checked={withTitlesCross} onChange={(e) => setWithTitlesCross(e.target.checked)} />
                    Cross structural cartesian with all 5 titles (×5 rows)
                </label>
                <p style={{ fontSize: 13, color: '#888', marginBottom: 0 }}>
                    Selected: <strong>{selectedCases.length}</strong> permutations — that many real dev rows will be created.
                </p>
            </div>

            <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
                <button
                    onClick={runTests}
                    disabled={running || selectedCases.length === 0}
                    style={{
                        padding: '12px 22px',
                        fontSize: 16,
                        fontWeight: 700,
                        borderRadius: 10,
                        border: 'none',
                        background: running || selectedCases.length === 0 ? '#999' : '#1a1a2e',
                        color: '#fff',
                        cursor: running || selectedCases.length === 0 ? 'default' : 'pointer',
                    }}
                >
                    {running ? 'Running…' : `Run ${selectedCases.length} permutations`}
                </button>
                {results.length > 0 && !running && (
                    <button
                        onClick={copyResults}
                        style={{ padding: '12px 18px', fontSize: 14, borderRadius: 10, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
                    >
                        Copy results (TSV)
                    </button>
                )}
            </div>

            {progress && <p style={{ marginTop: 12, color: '#1a1a2e', fontSize: 13 }}>{progress}</p>}

            {results.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <p style={{ fontWeight: 700 }}>
                        {passed.length} reach e-sign · {blocked.length} blocked · {results.length} total
                        {regressions.length > 0 && (
                            <span style={{ color: '#c0392b' }}> · {regressions.length} unexpected (≠ expectation)</span>
                        )}
                    </p>

                    {regressions.length > 0 && (
                        <div style={{ ...card, borderColor: '#f5c6cb', background: '#fff6f6' }}>
                            <h3 style={{ color: '#c0392b', marginTop: 0 }}>Unexpected results (actual ≠ expected)</h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                                        <th style={{ padding: 6 }}>Group</th>
                                        <th style={{ padding: 6 }}>Variation</th>
                                        <th style={{ padding: 6 }}>Expected</th>
                                        <th style={{ padding: 6 }}>Status</th>
                                        <th style={{ padding: 6 }}>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {regressions.map((r, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f3f3f3' }}>
                                            <td style={{ padding: 6, color: '#888' }}>{r.group}</td>
                                            <td style={{ padding: 6 }}>{r.label}</td>
                                            <td style={{ padding: 6 }}>{r.expectPass ? 'reach e-sign' : 'blocked'}</td>
                                            <td style={{ padding: 6 }}>{r.status}</td>
                                            <td style={{ padding: 6, color: '#c0392b' }}>{r.message}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {blocked.length > 0 && (
                        <div style={card}>
                            <h3 style={{ color: '#c0392b', marginTop: 0 }}>Blocked before e-sign ({blocked.length})</h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                                        <th style={{ padding: 6 }}>Group</th>
                                        <th style={{ padding: 6 }}>Variation</th>
                                        <th style={{ padding: 6 }}>Status</th>
                                        <th style={{ padding: 6 }}>Reason</th>
                                        <th style={{ padding: 6 }}>ms</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {blocked.map((r, idx) => (
                                        <tr
                                            key={idx}
                                            style={{
                                                borderBottom: '1px solid #f3f3f3',
                                                background: r.matchedExpectation === false ? '#fff6f6' : '#fff',
                                            }}
                                        >
                                            <td style={{ padding: 6, color: '#888' }}>{r.group}</td>
                                            <td style={{ padding: 6 }}>{r.label}</td>
                                            <td style={{ padding: 6 }}>{r.status}</td>
                                            <td style={{ padding: 6, color: '#c0392b' }}>{r.message}</td>
                                            <td style={{ padding: 6 }}>{r.ms}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div style={card}>
                        <h3 style={{ color: '#27ae60', marginTop: 0 }}>Reached e-sign ({passed.length})</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <tbody>
                                {passed.map((r, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #f3f3f3' }}>
                                        <td style={{ padding: 6, color: '#888' }}>{r.group}</td>
                                        <td style={{ padding: 6 }}>✅ {r.label}</td>
                                        <td style={{ padding: 6 }}>{r.status}</td>
                                        <td style={{ padding: 6 }}>{r.ms} ms</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

export default TestRunner
